import { useState, useEffect, useRef } from "react";
import { X, Trash2, ChevronDown, ChevronUp, Bug } from "lucide-react";

interface LogEntry {
  id: number;
  type: "log" | "warn" | "error" | "info";
  message: string;
  timestamp: Date;
}

/**
 * Встроенная debug консоль для отладки на iPad/мобильных устройствах.
 * Перехватывает console.log/warn/error и показывает их в UI.
 *
 * Активация: тройной тап в правом нижнем углу экрана
 */
export function DebugConsole() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [tapCount, setTapCount] = useState(0);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const idCounter = useRef(0);

  // Перехват console методов
  useEffect(() => {
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
    };

    const addLog = (type: LogEntry["type"], args: any[]) => {
      const message = args
        .map((arg) => {
          if (typeof arg === "object") {
            try {
              return JSON.stringify(arg, null, 2);
            } catch {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(" ");

      // Откладываем обновление состояния чтобы не вызывать setState во время рендера другого компонента
      queueMicrotask(() => {
        setLogs((prev) => [
          ...prev.slice(-100), // Храним последние 100 логов
          {
            id: idCounter.current++,
            type,
            message,
            timestamp: new Date(),
          },
        ]);
      });
    };

    console.log = (...args) => {
      originalConsole.log(...args);
      addLog("log", args);
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      addLog("warn", args);
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      addLog("error", args);
    };

    console.info = (...args) => {
      originalConsole.info(...args);
      addLog("info", args);
    };

    // Перехват глобальных ошибок
    const handleError = (event: ErrorEvent) => {
      addLog("error", [`[Global Error] ${event.message} at ${event.filename}:${event.lineno}`]);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      addLog("error", [`[Unhandled Promise] ${event.reason}`]);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      console.info = originalConsole.info;
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  // Автоскролл к последнему логу
  useEffect(() => {
    if (isOpen && !isMinimized) {
      logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, isOpen, isMinimized]);

  // Тройной тап для активации
  useEffect(() => {
    const handleTap = (e: TouchEvent) => {
      // Проверяем, что тап в правом нижнем углу (100x100px)
      const touch = e.touches[0];
      const isBottomRight =
        touch.clientX > window.innerWidth - 100 &&
        touch.clientY > window.innerHeight - 100;

      if (!isBottomRight) {
        setTapCount(0);
        return;
      }

      setTapCount((prev) => prev + 1);

      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }

      tapTimeoutRef.current = setTimeout(() => {
        setTapCount(0);
      }, 500);
    };

    document.addEventListener("touchstart", handleTap);
    return () => document.removeEventListener("touchstart", handleTap);
  }, []);

  // Открытие при тройном тапе
  useEffect(() => {
    if (tapCount >= 3) {
      setIsOpen(true);
      setTapCount(0);
    }
  }, [tapCount]);

  const clearLogs = () => setLogs([]);

  const getLogColor = (type: LogEntry["type"]) => {
    switch (type) {
      case "error":
        return "text-red-600 bg-red-50";
      case "warn":
        return "text-yellow-700 bg-yellow-50";
      case "info":
        return "text-blue-600 bg-blue-50";
      default:
        return "text-gray-700 bg-gray-50";
    }
  };

  const getLogPrefix = (type: LogEntry["type"]) => {
    switch (type) {
      case "error":
        return "[ERROR]";
      case "warn":
        return "[WARN]";
      case "info":
        return "[INFO]";
      default:
        return "[LOG]";
    }
  };

  if (!isOpen) {
    return (
      // Невидимая зона для тройного тапа + индикатор
      // pointer-events-none чтобы не блокировать клики на AI кнопку
      <div className="fixed bottom-0 right-0 w-24 h-24 z-[9999] pointer-events-none">
        {tapCount > 0 && (
          <div className="absolute bottom-2 right-2 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center text-xs font-bold">
            {tapCount}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`fixed z-[9999] bg-white border border-gray-300 shadow-2xl rounded-t-xl transition-all duration-200 ${
        isMinimized
          ? "bottom-0 left-0 right-0 h-10"
          : "bottom-0 left-0 right-0 h-[50vh]"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 border-b rounded-t-xl">
        <div className="flex items-center gap-2">
          <Bug size={16} className="text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">
            Debug Console ({logs.length})
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearLogs}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            title="Очистить"
          >
            <Trash2 size={16} className="text-gray-600" />
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            title={isMinimized ? "Развернуть" : "Свернуть"}
          >
            {isMinimized ? (
              <ChevronUp size={16} className="text-gray-600" />
            ) : (
              <ChevronDown size={16} className="text-gray-600" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            title="Закрыть"
          >
            <X size={16} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Logs */}
      {!isMinimized && (
        <div className="flex-1 overflow-y-auto h-[calc(50vh-40px)] p-2 space-y-1 font-mono text-xs">
          {logs.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              Логи появятся здесь...
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className={`p-2 rounded ${getLogColor(log.type)}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 flex-shrink-0">
                    {log.timestamp.toLocaleTimeString("ru-RU", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </span>
                  <span className="font-semibold flex-shrink-0">
                    {getLogPrefix(log.type)}
                  </span>
                  <span className="whitespace-pre-wrap break-all">
                    {log.message}
                  </span>
                </div>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      )}
    </div>
  );
}
